import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle, Key, FloppyDisk, Trash, Warning, Lightning, Storefront, Link as LinkIcon,
} from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useFeeRates, fmtPct } from "@/hooks/useFeeRates";

const inp = "w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 focus:border-[var(--accent)] outline-none font-mono text-sm";

export default function Settings() {
  const [status, setStatus] = useState({ connected: false, preview: "", public_key: "", connection_type: null });
  const [oauth, setOauth] = useState({ enabled: false });
  const [token, setToken] = useState("");
  const [pub, setPub] = useState("");
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingOauth, setLoadingOauth] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const { rates } = useFeeRates();
  const salesFeeLabel = fmtPct(rates.sales_commission_rate);

  const load = async () => {
    try {
      const [{ data: st }, { data: ostat }] = await Promise.all([
        api.get("/user/mp-token"),
        api.get("/oauth/mercadopago/status"),
      ]);
      setStatus(st);
      setPub(st.public_key || "");
      setOauth(ostat);
    } catch (e) { console.warn("settings load failed", e?.message); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!token) return toast.error("Cole seu Access Token");
    setLoadingSave(true);
    try {
      await api.post("/user/mp-token", { access_token: token, public_key: pub });
      toast.success("Conta Mercado Pago conectada!");
      setToken("");
      load();
    } catch (e) { toast.error(errMsg(e, "Erro ao conectar")); }
    finally { setLoadingSave(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Desconectar sua conta Mercado Pago?")) return;
    await api.delete("/user/mp-token");
    toast.success("Conta desconectada");
    setStatus({ connected: false, preview: "", public_key: "", connection_type: null });
  };

  const connectOauth = async () => {
    setLoadingOauth(true);
    try {
      const { data } = await api.post("/oauth/mercadopago/authorize");
      // Guarda state (double-check) e o redirect_uri esperado no browser
      sessionStorage.setItem("vb_mp_oauth_state", data.state);
      const url = new URL(data.authorize_url);
      sessionStorage.setItem(
        "vb_mp_oauth_redirect_uri",
        url.searchParams.get("redirect_uri") || "",
      );
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(errMsg(e, "Não foi possível iniciar OAuth"));
      setLoadingOauth(false);
    }
  };

  return (
    <div className="fadeUp max-w-3xl">
      <div className="label-caps mb-2">Configurações</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-3">Conectar Mercado Pago</h1>
      <p className="text-white/60 mb-8">
        Conecte sua conta Mercado Pago para que cada venda dos seus links vá <b className="text-white">direto para você</b>.
        A plataforma retém a comissão de <b className="text-[var(--accent)]" data-testid="settings-sales-fee">{salesFeeLabel}</b> automaticamente via <b className="text-[var(--accent)]">application_fee</b>.
      </p>

      {/* Status card */}
      <div className="card-flat p-6 mb-6" data-testid="mp-status-card">
        <div className="flex items-center gap-3 mb-4">
          <div
            className={`w-10 h-10 rounded-sm border flex items-center justify-center ${
              status.connected
                ? "border-[var(--success)] text-[var(--success)]"
                : "border-[var(--border)] text-white/40"
            }`}
          >
            {status.connected ? <CheckCircle size={22} weight="fill" /> : <Warning size={22} />}
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg" data-testid="mp-connected-label">
              {status.connected ? "Mercado Pago conectado" : "Não conectado"}
            </div>
            <div className="text-xs text-white/50">
              {status.connected ? (
                <>
                  Token: <code className="text-[var(--accent)]" data-testid="mp-user-preview">{status.preview}</code>
                  {status.mp_user_id && (
                    <> · MP user: <code className="text-white/70">{status.mp_user_id}</code></>
                  )}
                </>
              ) : (
                "Suas vendas ainda passam pela conta MP da plataforma."
              )}
            </div>
            {status.connection_type && (
              <div className="mt-1 inline-block text-[10px] px-2 py-0.5 rounded-sm border border-[var(--accent)]/40 text-[var(--accent)]"
                   data-testid="mp-conn-type">
                {status.connection_type === "oauth" ? "OAuth Marketplace (split automático)" : "Token manual"}
              </div>
            )}
          </div>
        </div>

        {status.connected && (
          <button
            onClick={disconnect}
            data-testid="disconnect-mp"
            className="btn-ghost text-sm text-[var(--danger)] flex items-center gap-2"
          >
            <Trash size={14} /> Desconectar
          </button>
        )}
      </div>

      {!status.connected && (
        <>
          {/* OAuth card (primary CTA) */}
          {oauth.enabled ? (
            <div
              className="card-flat p-6 mb-6 bg-gradient-to-br from-[var(--accent)]/10 to-transparent border-[var(--accent)]/40"
              data-testid="oauth-card"
            >
              <div className="flex items-start gap-4 mb-4">
                <Storefront size={32} color="var(--accent)" weight="duotone" />
                <div className="flex-1">
                  <div className="font-display font-bold text-xl">Conectar via OAuth (recomendado)</div>
                  <div className="text-sm text-white/60 mt-1">
                    Autorize a MercadoPagu em 1 clique. O dinheiro cai <b className="text-white">direto na sua conta MP</b>,
                    e a comissão de {salesFeeLabel} é descontada automaticamente pelo Mercado Pago via <code className="text-[var(--accent)]">application_fee</code>.
                  </div>
                </div>
              </div>
              <ul className="text-xs text-white/60 space-y-1 mb-4">
                <li className="flex items-start gap-2"><Lightning size={12} className="mt-0.5 text-[var(--accent)]" /> Sem copiar/colar chaves</li>
                <li className="flex items-start gap-2"><Lightning size={12} className="mt-0.5 text-[var(--accent)]" /> Renovação automática do token (refresh)</li>
                <li className="flex items-start gap-2"><Lightning size={12} className="mt-0.5 text-[var(--accent)]" /> Split real, sem repasse manual</li>
              </ul>
              <button
                onClick={connectOauth}
                disabled={loadingOauth}
                data-testid="connect-mp-oauth"
                className="btn-primary w-full flex items-center justify-center gap-2 py-3"
              >
                <LinkIcon size={16} weight="bold" />
                {loadingOauth ? "Redirecionando..." : "Conectar Mercado Pago"}
              </button>
              <button
                onClick={() => setShowManual((v) => !v)}
                className="mt-3 w-full text-xs text-white/50 hover:text-white/80 underline"
                data-testid="toggle-manual"
              >
                {showManual ? "Ocultar" : "Ou colar Access Token manualmente"}
              </button>
            </div>
          ) : (
            <div className="card-flat p-4 mb-6 text-sm text-white/60 border-[var(--danger)]/40" data-testid="oauth-disabled">
              OAuth do MP não configurado no servidor — use o método manual abaixo.
            </div>
          )}

          {(!oauth.enabled || showManual) && (
            <div className="card-flat p-6" data-testid="manual-card">
              <div className="font-display font-bold text-lg mb-1">Método manual</div>
              <div className="text-xs text-white/50 mb-4">Cole o Access Token da sua aplicação MP.</div>
              <div className="space-y-3">
                <div>
                  <label className="label-caps block mb-2">Access Token (APP_USR-...)</label>
                  <input
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    data-testid="user-mp-token"
                    className={inp}
                    placeholder="APP_USR-xxxx..."
                  />
                </div>
                <div>
                  <label className="label-caps block mb-2">Public Key (opcional)</label>
                  <input
                    value={pub}
                    onChange={(e) => setPub(e.target.value)}
                    data-testid="user-mp-public"
                    className={inp}
                    placeholder="APP_USR-xxxx..."
                  />
                </div>
                <button
                  onClick={save}
                  disabled={loadingSave}
                  data-testid="save-user-mp"
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  <FloppyDisk size={16} weight="bold" />
                  {loadingSave ? "Salvando..." : "Conectar minha conta"}
                </button>
              </div>
              <div className="mt-6 border-t border-[var(--border)] pt-4 text-xs text-white/50 space-y-1">
                <div className="flex items-center gap-2">
                  <Key size={12} /> Onde encontrar suas chaves?
                </div>
                <div>1. Acesse <a href="https://www.mercadopago.com.br/developers/panel" target="_blank" rel="noreferrer" className="text-[var(--accent)] underline">mercadopago.com.br/developers</a></div>
                <div>2. Crie uma aplicação → &quot;Credenciais de produção&quot;</div>
                <div>3. Copie o <b>Access Token</b> e cole aqui</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
